import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Clock, ChevronDown, CheckCircle, ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const FAQ_ITEMS = [
  {
    question: "How do I reset my password?",
    answer: 'Use the "Forgot Password" option from the login screen. You\'ll receive an email with instructions to set a new password.',
  },
  {
    question: "How do I manage my subscription?",
    answer: "Visit clientopsapp.com and log into your account. Open Profile \u2192 Billing to view, change, or cancel your subscription at any time.",
  },
  {
    question: "How do I delete my account?",
    answer: "Open the ClientOps mobile app and navigate to Profile \u2192 Account \u2192 Delete Account. Your data will be permanently removed in accordance with our privacy policy.",
  },
  {
    question: "How do I create and send an invoice?",
    answer: "Open Financials \u2192 Invoices \u2192 Create Invoice. Fill in the client and line items, then generate and send the invoice directly from your device. You can also customize your invoice template (logo, colors, payment options) from Settings \u2192 Invoice Template.",
  },
  {
    question: "Does ClientOps use AI?",
    answer: "Yes. ClientOps includes an AI Assistant that can help generate client communications, follow-up messages, and business insights. The AI never makes changes to your data without your explicit confirmation.",
  },
  {
    question: "How do I contact support?",
    answer: "Use the contact form below or email support@clientopsapp.com directly. We typically respond within 1\u20132 business days.",
  },
];

const FAQItem = ({ item, index, isOpen, onToggle }) => (
  <div className="border border-slate-200 rounded-2xl bg-white overflow-hidden" data-testid={`faq-toggle-${index}`}>
    <button
      onClick={onToggle}
      className="w-full flex items-center justify-between p-5 text-left hover:bg-slate-50 transition-colors"
    >
      <span className="font-medium text-slate-900 text-sm sm:text-base pr-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
        {item.question}
      </span>
      <ChevronDown className={`w-5 h-5 text-slate-400 flex-shrink-0 transition-transform duration-200 ${isOpen ? "rotate-180" : ""}`} />
    </button>
    {isOpen && (
      <div className="px-5 pb-5">
        <p className="text-sm text-slate-600 leading-relaxed">{item.answer}</p>
      </div>
    )}
  </div>
);

const ContactForm = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [errors, setErrors] = useState({});
  const [submitted, setSubmitted] = useState(false);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Name is required";
    if (!form.email.trim()) e.email = "Email is required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    if (!form.subject.trim()) e.subject = "Subject is required";
    if (!form.message.trim()) e.message = "Message is required";
    else if (form.message.trim().length < 10) e.message = "Message must be at least 10 characters";
    return e;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    const subject = `[ClientOps Support] ${form.subject}`;
    const body = [
      `Name: ${form.name}`,
      `Email: ${form.email}`,
      "",
      "\u2014 Message \u2014",
      form.message,
      "",
      "\u2014 Sent from clientopsapp.com/support \u2014",
    ].join("\n");
    window.location.href = `mailto:support@clientopsapp.com?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center shadow-sm" data-testid="form-success">
        <div className="w-12 h-12 bg-[#22C55E]/10 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="w-6 h-6 text-[#22C55E]" />
        </div>
        <h3 className="text-lg font-semibold text-slate-900 mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          Email opened in your mail app
        </h3>
        <p className="text-sm text-slate-500 mb-1">Hit Send in your mail app to deliver the message.</p>
        <p className="text-sm text-slate-500 mb-5">We'll respond within 1-2 business days.</p>
        <button
          onClick={() => { setSubmitted(false); setForm({ name: "", email: "", subject: "", message: "" }); setErrors({}); }}
          className="text-sm font-medium text-[#3B82F6] hover:text-[#2563EB] transition-colors"
        >
          Send another message
        </button>
      </div>
    );
  }

  const inputClass = (field) =>
    `w-full px-4 py-2.5 text-sm rounded-lg border ${errors[field] ? "border-red-400 focus:ring-red-400" : "border-slate-200 focus:ring-[#3B82F6]"} focus:outline-none focus:ring-2 focus:ring-offset-0 bg-white text-slate-900 placeholder:text-slate-400 transition-colors`;

  return (
    <form onSubmit={handleSubmit} className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 shadow-sm" data-testid="contact-form-section">
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Name</label>
          <input
            type="text"
            className={inputClass("name")}
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            data-testid="form-name"
          />
          {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Email</label>
          <input
            type="email"
            className={inputClass("email")}
            placeholder="you@example.com"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            data-testid="form-email"
          />
          {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Subject</label>
          <input
            type="text"
            className={inputClass("subject")}
            placeholder="What can we help with?"
            value={form.subject}
            onChange={(e) => setForm({ ...form, subject: e.target.value })}
            data-testid="form-subject"
          />
          {errors.subject && <p className="text-xs text-red-500 mt-1">{errors.subject}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1.5">Message</label>
          <textarea
            rows={5}
            className={inputClass("message")}
            placeholder="Describe your issue or question..."
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            data-testid="form-message"
          />
          {errors.message && <p className="text-xs text-red-500 mt-1">{errors.message}</p>}
        </div>
      </div>
      <Button
        type="submit"
        className="mt-6 bg-[#3B82F6] hover:bg-[#2563EB] text-white rounded-lg shadow-lg shadow-[#3B82F6]/25 px-6 h-11 font-medium"
        data-testid="form-submit-btn"
      >
        Send message
      </Button>
    </form>
  );
};

export default function Support() {
  const [openFaq, setOpenFaq] = useState(0);

  return (
    <div className="min-h-screen bg-white" data-testid="support-page">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-lg border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2">
              <img
                src="https://customer-assets.emergentagent.com/job_ops-portal-8/artifacts/a8dmjt8s_clientops-ios-1024.png"
                alt="ClientOps Logo"
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-lg font-semibold text-slate-900" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
                ClientOps<sup className="text-[10px] text-slate-400 ml-0.5">&trade;</sup>
              </span>
            </Link>
            <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors">
              <ArrowLeft className="w-4 h-4" /> Back to Home
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-28 pb-16 bg-gradient-to-b from-[#F7F8FA] to-white">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h1 className="text-4xl sm:text-5xl font-semibold text-slate-900 tracking-tight mb-4" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            ClientOps Support
          </h1>
          <p className="text-lg text-slate-500">Need help with ClientOps? We're here to help.</p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="bg-[#F7F8FA] rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center mb-3">
                <Mail className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <p className="text-xs font-semibold text-slate-400 tracking-wider mb-1">EMAIL</p>
              <a
                href="mailto:support@clientopsapp.com"
                className="text-base font-medium text-[#3B82F6] hover:text-[#2563EB] transition-colors"
                data-testid="support-email-link"
              >
                support@clientopsapp.com
              </a>
            </div>
            <div className="bg-[#F7F8FA] rounded-2xl border border-slate-200 p-6 shadow-sm hover:shadow-lg transition-shadow">
              <div className="w-10 h-10 bg-[#3B82F6]/10 rounded-xl flex items-center justify-center mb-3">
                <Clock className="w-5 h-5 text-[#3B82F6]" />
              </div>
              <p className="text-xs font-semibold text-slate-400 tracking-wider mb-1">RESPONSE TIME</p>
              <p className="text-base font-medium text-slate-900">Within 1-2 business days</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F7F8FA]" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-6">
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mb-8 text-center" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            Frequently Asked Questions
          </h2>
          <div className="space-y-3">
            {FAQ_ITEMS.map((item, i) => (
              <FAQItem
                key={i}
                item={item}
                index={i}
                isOpen={openFaq === i}
                onToggle={() => setOpenFaq(openFaq === i ? -1 : i)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 bg-white">
        <div className="max-w-xl mx-auto px-6">
          <div className="text-center mb-8">
            <h2 className="text-2xl sm:text-3xl font-semibold text-slate-900 tracking-tight mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
              Contact us
            </h2>
            <p className="text-sm text-slate-500">Submitting this form opens your default email client pre-filled — just hit Send to deliver it to our team.</p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-slate-900 border-t border-slate-800">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-slate-500 text-sm">ClientOps is a product of Noble Strategies Consulting, LLC.</p>
          <p className="text-slate-500 text-sm">&copy; 2026 Noble Strategies Consulting, LLC. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
